import {
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    OnDestroy,
    OnInit,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import {
    CompanyController,
    DocumentController,
    DocumentEntity,
    DocumentImportController,
    DocumentStatusEnum,
    FeatureFlagController,
    FeedGroupEntity,
    FeedItemEntity,
    FeedItemTypeEnum,
    OrderController,
    OrderEntity,
} from '@domain';
import { OnlineUserPartial } from 'src/app/domain/entities/partials/online-user.partial';
import { PusherService } from 'src/app/shared/utils/pusher/pusher.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
    RaButtonComponent,
    RaInputComponent,
    RaLoadingComponent,
    RaToastService,
} from '@shared/components';
import {
    DialogConfirmationControl,
    RaBadgeComponent,
    RaDialogConfirmationService,
    RaIconComponent,
    RaMenuComponent,
    RaMenuItem,
    RaOptionItem,
    RaOptionListComponent,
    RaProgressBarComponent,
    RaSkeletonComponent,
    RaTooltipDirective,
} from 'ra-ui';
import { debounceTime, distinctUntilChanged, EMPTY, switchMap, take } from 'rxjs';
import { RaClickOutsideDirective } from 'src/app/shared/directives/ra-click-outside/ra-click-outside.directive';
import { OrderCreateComponent } from 'src/app/modules/order/components/create/order-create.component';
import { ImportDocumentsModalComponent } from 'src/app/modules/order/components/import-documents-modal/import-documents-modal.component';
import { RaAvatarsListComponent } from 'src/app/shared/components/ra-avatars-list/ra-avatars-list.component';
import { RaEmptyComponent } from 'src/app/shared/components/ra-empty/ra-empty.component';
import { RaModalComponent } from 'src/app/shared/components/ra-modal/ra-modal.component';
import { RaModalListOrdersComponent } from 'src/app/shared/components/ra-modal-list-orders/ra-modal-list-orders.component';
import { RaSquareIconComponent } from 'src/app/shared/components/ra-square-icon/ra-square-icon.component';
import { FormatFileSizePipe } from 'src/app/shared/pipes/format-file-size.pipe';
import { IconByDocumentTypePipe } from 'src/app/shared/pipes/documents/icon-by-document-type.pipe';
import { IconColorByDocumentTypePipe } from 'src/app/shared/pipes/documents/icon-color-by-document-type.pipe';
import { importGenerationPercent } from 'src/app/modules/order/import-progress.util';
import { I18nService } from 'src/app/shared/services/i18n.service';
import { DocumentBatchService } from 'src/app/shared/services/document-batch.service';
import { OrderArchivedPanelComponent } from '../order-archived-panel/order-archived-panel.component';
import { OrderTrashPanelComponent } from '../order-trash-panel/order-trash-panel.component';
import {
    FEED_GROUP_COLORS,
    FEED_GROUP_NONE,
    FeedGroupService,
} from '../../services/feed-group.service';
import { FeedMainService } from '../../services/feed-main.service';
import { OrderMainService } from '../../services/order-main.service';

/**
 * Uma seção da lista: um grupo do usuário (ou o "sem grupo") com os itens do
 * feed já carregados que pertencem a ele.
 */
interface FeedSection {
    /** Id do grupo ou `FEED_GROUP_NONE`. */
    id: string;
    name: string;
    color?: string;
    group?: FeedGroupEntity;
    items: FeedItemEntity[];
    collapsed: boolean;
}

/**
 * Lista do FEED unificado (feature `unified_feed`): serviços (GROUP) e
 * documentos avulsos (DOCUMENT) numa lista só, estilo WhatsApp, organizada em
 * GRUPOS nomeados pelo usuário (trilho lateral + seções recolhíveis) e com o
 * menu "+ Novo" (IA, documento avulso, serviço ou importação). Substitui o
 * `order-list` na coluna esquerda quando a flag libera — o master-detail
 * continua o mesmo (grupo selecionado abre o detalhe à direita).
 *
 * Grupos: o trilho à esquerda lista "Todos" + um botão por grupo + "sem grupo".
 * Em "Todos" a lista vem seccionada (os itens carregados, repartidos pelo mapa
 * order → grupo); num grupo específico o backend já filtra e a lista é plana.
 * Mover é por arraste (linha → seção ou botão do trilho) ou pelo menu da pasta
 * na linha, que também serve a quem não arrasta (toque/teclado).
 */
@Component({
    selector: 'feed-list',
    templateUrl: 'feed-list.component.html',
    styleUrl: 'feed-list.component.scss',
    imports: [
        RaAvatarsListComponent,
        RaEmptyComponent,
        RaInputComponent,
        RaLoadingComponent,
        ReactiveFormsModule,
        RaButtonComponent,
        RaIconComponent,
        RaBadgeComponent,
        RaOptionListComponent,
        RaProgressBarComponent,
        RaSkeletonComponent,
        RaTooltipDirective,
        RaMenuComponent,
        RaClickOutsideDirective,
        RaModalComponent,
        RaModalListOrdersComponent,
        RaSquareIconComponent,
        OrderCreateComponent,
        ImportDocumentsModalComponent,
        OrderArchivedPanelComponent,
        OrderTrashPanelComponent,
        TranslatePipe,
        FormatFileSizePipe,
        IconByDocumentTypePipe,
        IconColorByDocumentTypePipe,
    ],
})
export class FeedListComponent implements OnInit, OnDestroy {
    public searchControl = new FormControl('');
    public isModalCreateOrderVisible = signal<boolean>(false);
    public isArchivedPanelVisible = signal<boolean>(false);
    public isTrashPanelVisible = signal<boolean>(false);

    /** Menu "···" com as utilidades (arquivados/lixeira). */
    public isMoreMenuVisible = signal<boolean>(false);

    /** Modal de importação de arquivos (gera documento avulso). */
    public isImportDocumentsVisible = signal<boolean>(false);

    /** Loading do clique num grupo (busca a order completa antes de abrir). */
    public openingItemId = signal<string | null>(null);

    public feedItemTypeEnum = FeedItemTypeEnum;
    public readonly groupNone = FEED_GROUP_NONE;
    public readonly groupColors = FEED_GROUP_COLORS;

    // Placeholders exibidos enquanto a primeira página carrega
    protected readonly skeletons = Array.from({ length: 8 });

    // ── Grupos ─────────────────────────────────────────────────────────

    /** Id da linha sendo arrastada (vira um espaço tracejado na lista). */
    public draggingId = signal<string | null>(null);

    /** Linha pressionada (clique segurado) antes do arraste começar: "levanta". */
    public pressingId = signal<string | null>(null);

    /** Alvo do arraste em cima (seção ou botão do trilho): ganha o realce. */
    public dropTarget = signal<string | null>(null);

    /** Nome do alvo sob o cursor, para o aviso "Soltar em …" no topo da lista. */
    public dropTargetName = computed(() => {
        const id = this.dropTarget();
        if (!id) return null;
        if (id === FEED_GROUP_NONE) return this._t('order.feed.groups.none');

        return this.feedGroupService.byId(id)?.name ?? null;
    });

    public dropTargetColor = computed(
        () => this.feedGroupService.byId(this.dropTarget())?.color ?? null
    );

    /**
     * Menu "···" da linha: qual item e em que nível (raiz com as ações, ou a
     * lista de grupos do "mover para grupo").
     */
    public rowMenu = signal<{ itemId: string; level: 'root' | 'groups' } | null>(null);

    /** Documento avulso escolhendo a ordem de serviço de destino (modal). */
    public documentToMove = signal<FeedItemEntity | null>(null);

    /** Linha que acabou de cair num grupo: pisca para mostrar onde foi parar. */
    public landedId = signal<string | null>(null);

    /** Botão do trilho que acabou de receber um item: dá um "pop". */
    public poppedTargetId = signal<string | null>(null);

    /** Modal de criar/editar grupo. `group` ausente = criando. */
    public groupModal = signal<{ group?: FeedGroupEntity } | null>(null);
    public groupNameControl = new FormControl('', [Validators.required, Validators.maxLength(60)]);
    public groupColor = signal<string | null>(null);
    public savingGroup = signal<boolean>(false);

    /**
     * Item que pediu "Novo grupo…" no menu da linha: quando o grupo nascer, o
     * item vai direto para ele.
     */
    private _pendingMoveOrderId: string | null = null;

    /**
     * A lista como seções. Em "Todos" é um bloco por grupo (na ordem do trilho)
     * + "sem grupo" no fim, repartindo os itens JÁ CARREGADOS; num grupo
     * específico é uma seção só, com tudo que o backend filtrou.
     */
    protected sections = computed<FeedSection[]>(() => {
        const items = this.feedMainService.items();
        const selected = this.feedMainService.groupId();
        const collapsed = this.feedGroupService.collapsed();
        const noneName = this._t('order.feed.groups.none');

        if (selected === FEED_GROUP_NONE) {
            return [{ id: FEED_GROUP_NONE, name: noneName, items, collapsed: false }];
        }

        if (selected) {
            const group = this.feedGroupService.byId(selected);

            return [
                {
                    id: selected,
                    name: group?.name ?? '',
                    color: group?.color,
                    group,
                    items,
                    collapsed: false,
                },
            ];
        }

        const byOrder = this.feedGroupService.groupByOrder();
        const buckets = new Map<string, FeedItemEntity[]>();
        for (const item of items) {
            const key = byOrder.get(item._id) ?? FEED_GROUP_NONE;
            if (!buckets.has(key)) buckets.set(key, []);
            buckets.get(key)!.push(item);
        }

        const sections: FeedSection[] = this.feedGroupService.groups().map(group => ({
            id: group._id,
            name: group.name,
            color: group.color,
            group,
            items: buckets.get(group._id) ?? [],
            collapsed: collapsed.has(group._id),
        }));

        sections.push({
            id: FEED_GROUP_NONE,
            name: noneName,
            items: buckets.get(FEED_GROUP_NONE) ?? [],
            collapsed: collapsed.has(FEED_GROUP_NONE),
        });

        return sections;
    });

    /** Título da modal de grupo: criar ou editar. */
    public groupModalTitle = computed(() =>
        this.groupModal()?.group ? 'order.feed.groups.edit' : 'order.feed.groups.new'
    );

    /** Modal "···": utilidades (novo grupo / arquivados / lixeira). */
    protected moreOptions = computed<RaOptionItem[]>(() => {
        return [
            {
                value: 'new-group',
                title: this._t('order.feed.groups.new'),
                hint: this._t('order.feed.groups.newHint'),
                icon: 'folder',
                tone: 'primary',
            },
            {
                value: 'archived',
                title: this._t('order.status.archived'),
                hint: this._t('order.feed.archivedHint'),
                icon: 'archive',
                tone: 'primary',
            },
            {
                value: 'trash',
                title: this._t('order.trashPanel.title'),
                hint: this._t('order.trashPanel.hint'),
                icon: 'trash',
                tone: 'danger',
            },
        ];
    });

    /** Timer do polling enquanto houver documento em geração via IA. */
    private _pollTimeout?: ReturnType<typeof setTimeout>;

    // * [Injects]
    public feedMainService = inject(FeedMainService);
    public feedGroupService = inject(FeedGroupService);
    public orderMainService = inject(OrderMainService);
    private _dialogConfirmation = inject(RaDialogConfirmationService);
    public featureFlagController = inject(FeatureFlagController);
    public batchService = inject(DocumentBatchService);
    private _orderController = inject(OrderController);
    private _documentController = inject(DocumentController);
    private _documentImportController = inject(DocumentImportController);
    private _toastService = inject(RaToastService);
    private _translate = inject(TranslateService);
    private _i18n = inject(I18nService);
    private _companyController = inject(CompanyController);
    private _pusher = inject(PusherService);
    private _destroyRef = inject(DestroyRef);
    private _host = inject<ElementRef<HTMLElement>>(ElementRef);

    /**
     * Açúcar para as listas acima — o texto vai pronto para o DS, que não traduz.
     * `I18nService.t` registra a dependência reativa, então as listas recomputam
     * tanto ao trocar de idioma quanto quando o dicionário termina de carregar.
     */
    private _t(key: string, params?: Record<string, unknown>): string {
        return this._i18n.t(key, params);
    }

    // Reage a QUALQUER atualização dos itens (inclusive reloads disparados pela
    // page, ex. pós-conclusão do chat) e re-agenda o polling se ainda houver
    // documento em geração — sem isso, só os getFeed do próprio componente
    // agendariam o polling.
    private _pollingEffect = effect(() => {
        this.feedMainService.items();
        this._schedulePollingIfGenerating();
    });

    ngOnInit(): void {
        this._listenSearchControl();
        this.getFeed();
        this.feedGroupService.load().subscribe();
        this._subscribeRealtime();
    }

    /**
     * Assina o canal da EMPRESA para refletir na hora os documentos gerados pela
     * IA, inclusive os avulsos.
     *
     * Por que o canal da empresa e não o da order: no avulso o `orderId` é de uma
     * order pessoal criada pelo backend no momento do Concluir, que o feed não
     * conhece. O canal da empresa também faz aparecer o documento gerado por
     * OUTRO usuário com a tela aberta. O polling continua como rede de segurança
     * (socket caído, aba voltando do background).
     */
    private _subscribeRealtime(): void {
        const companyId = this._companyController.getCurrentCompanyID();
        if (!companyId) return;

        // Placeholder criado / documento pronto / falha: em todos os casos a linha
        // do feed muda de estado. Recarrega em silêncio em vez de fazer upsert
        // manual, porque o item do feed é um agregado (order + doc + contagem)
        // que só o backend monta.
        for (const event of [
            'ia-chat-document-pending',
            'ia-chat-document-created',
            'ia-chat-document-error',
        ]) {
            this._pusher
                .subscribe(`COMPANY-${companyId}`, event)
                .pipe(takeUntilDestroyed(this._destroyRef))
                .subscribe(() =>
                    this.feedMainService.getFeed(this.searchControl.value, 1, true).subscribe()
                );
        }
    }

    ngOnDestroy(): void {
        clearTimeout(this._pollTimeout);
    }

    /**
     * Escuta as mudanças no input de busca
     */
    private _listenSearchControl(): void {
        this.searchControl.valueChanges
            // Sem `skip(1)`: `valueChanges` não emite o valor inicial, e pular o primeiro
            // evento fazia uma busca colada de uma vez só não disparar
            .pipe(debounceTime(600), distinctUntilChanged())
            .subscribe(() => this.getFeed());
    }

    /**
     * Busca o feed
     */
    public getFeed(page: number = 1): void {
        this.feedMainService
            .getFeed(this.searchControl.value, page)
            .subscribe(() => this._schedulePollingIfGenerating());
    }

    /**
     * Criação pedida de fora da lista (a tela sem seleção).
     *
     * Os modais vivem aqui, então quem pede só incrementa um tick no serviço. CONSOME e
     * ZERA, como o tick do chat na page: sem o reset, recriar a lista reabriria o modal
     * sozinho.
     */
    private _createRequestsEffect = effect(() => {
        if (this.feedMainService.newServiceTick() > 0) {
            this.feedMainService.newServiceTick.set(0);
            this.newService();
        }

        if (this.feedMainService.importTick() > 0) {
            this.feedMainService.importTick.set(0);
            this.newDocumentByImport();
        }
    });

    /**
     * Este serviço está com um pacote de PDFs sendo gerado?
     *
     * Vem do `DocumentBatchService`, que é quem acompanha o pacote da aba — a mesma fonte
     * da ilha do rodapé. Um pacote pedido por outra pessoa (ou em outra aba) não aparece
     * aqui: para isso seria preciso o servidor listar os pacotes vivos da empresa.
     */
    public isBatchRunning(item: FeedItemEntity): boolean {
        if (item.type !== FeedItemTypeEnum.GROUP) return false;
        if (!this.batchService.running()) return false;

        return this.batchService.batch()?.orderId?.toString() === item._id?.toString();
    }

    /** "3 de 40" — a mesma contagem que o modal e a ilha mostram. */
    public batchProgressLabel(): string {
        return this._translate.instant('order.documentBatch.feedProgress', {
            done: this.batchService.done(),
            total: this.batchService.total(),
        });
    }

    /**
     * Documento avulso ainda em geração via IA (worker assíncrono do backend)?
     * Enquanto sim: a linha mostra loading + aviso e o editor fica bloqueado.
     */
    public isGenerating(item: FeedItemEntity): boolean {
        if (item.type !== FeedItemTypeEnum.DOCUMENT) return false;

        const status = item.document?.status;

        return status === DocumentStatusEnum.PENDING || status === DocumentStatusEnum.CREATING;
    }

    /**
     * Progresso (2–100) da conversão do import, quando o backend já reportou algum.
     * Vem PERSISTIDO da API — por isso a barra continua de onde estava ao recarregar a
     * tela ou ao abrir em outro aparelho; o polling de 5s a mantém andando.
     * `null` = ainda na fila (o worker não pegou o job), aí mostramos só o spinner.
     */
    public generationPercent(item: FeedItemEntity): number | null {
        return importGenerationPercent(item.document);
    }

    /**
     * Rótulo da etapa atual ("lendo o arquivo", "gerando com IA"...). Numa geração
     * dividida em partes, acrescenta "(2/6)" — num arquivo de 200 páginas isso é o que
     * mostra que a coisa está andando mesmo quando o % sobe devagar.
     */
    public generationStageLabel(item: FeedItemEntity): string {
        const progress = item.document?.importGenerationProgress;
        if (!progress) return this._translate.instant('order.feed.generating');

        const stage = this._translate.instant(`order.feed.importStages.${progress.stage}`);

        return progress.totalParts && progress.totalParts > 1
            ? `${stage} (${progress.part}/${progress.totalParts})`
            : stage;
    }

    /** A geração via IA do documento avulso falhou? */
    public hasGenerationError(item: FeedItemEntity): boolean {
        return (
            item.type === FeedItemTypeEnum.DOCUMENT &&
            item.document?.status === DocumentStatusEnum.ERROR
        );
    }

    /**
     * Enquanto houver item em geração, revalida o feed em silêncio a cada 5s —
     * quando o worker termina, a linha troca o loading pelo documento pronto.
     */
    private _schedulePollingIfGenerating(): void {
        clearTimeout(this._pollTimeout);

        const hasGenerating = this.feedMainService.items().some(item => this.isGenerating(item));
        if (!hasGenerating) return;

        this._pollTimeout = setTimeout(() => {
            this.feedMainService
                .getFeed(this.searchControl.value, 1, true)
                .subscribe(() => this._schedulePollingIfGenerating());
        }, 5000);
    }

    /**
     * Arquiva um documento AVULSO: finish na order pessoal (endpoint existente
     * de arquivar serviço). O item sai do feed e passa ao painel de arquivados,
     * de onde pode ser restaurado (reopen).
     */
    /**
     * Cancela um import em andamento (travou ou desistência). O controller abre o
     * dialog de confirmação; ao cancelar de fato, a linha some na hora (o backend
     * soft-deleta o placeholder e o worker descarta o resultado).
     */
    public cancelImport(item: FeedItemEntity): void {
        this._documentImportController
            .cancel(item._id)
            .pipe(take(1))
            .subscribe({
                next: () => {
                    this.feedMainService.items.update(items =>
                        items.filter(i => i._id !== item._id)
                    );
                },
            });
    }

    /** Arquiva: avulso = finish na order pessoal; serviço = finish na própria order. */
    public archiveStandalone(item: FeedItemEntity): void {
        // Não marca a linha como loading aqui: `archive()` abre um dialog de
        // confirmação que, ao ser CANCELADO, completa sem emitir next/error — o
        // que deixava `openingItemId` preso e a linha eternamente "disabled".
        // O próprio dialog já mostra loading no botão; a linha só some no sucesso.
        this._orderController
            .archive(item._id)
            .pipe(take(1))
            .subscribe({
                next: () => {
                    this.feedMainService.items.update(items =>
                        items.filter(i => i._id !== item._id)
                    );
                },
            });
    }

    // ── Grupos: trilho, seções e arraste ───────────────────────────────

    /**
     * Troca o grupo do trilho (`null` = Todos). O backend filtra, então recarrega —
     * e esvazia a lista antes, senão os itens do grupo anterior ficavam parados na
     * tela até a resposta chegar, sem nenhum sinal de que algo estava carregando.
     */
    public selectGroup(groupId: string | null): void {
        if (!this.feedMainService.setGroup(groupId)) return;

        this.feedMainService.items.set([]);
        this.getFeed();
    }

    /**
     * Clique no cabeçalho abre/fecha a seção — inclusive na seta, que não tem
     * handler próprio de propósito. Fora: o nome (renomeia) e o "···" (modal).
     */
    public onSectionHeadClick(event: MouseEvent, section: FeedSection): void {
        const target = event.target as HTMLElement;
        if (target.closest('.feed-group__name, .feed-group__more, .feed-group__rename')) return;
        if (this.feedMainService.groupId() !== null) return;
        this.toggleSection(section);
    }

    // ── Renomear inline ────────────────────────────────────────────────

    /** Seção com o nome em edição (clique no nome). */
    public renamingId = signal<string | null>(null);

    public startRename(section: FeedSection): void {
        if (!section.group) return;
        this.renamingId.set(section.id);
        // O input só existe depois da renderização
        setTimeout(() => {
            const input =
                this._host.nativeElement.querySelector<HTMLInputElement>('.feed-group__rename');
            input?.focus();
            input?.select();
        });
    }

    public commitRename(section: FeedSection, value: string): void {
        if (this.renamingId() !== section.id) return;
        this.renamingId.set(null);

        const name = value.trim();
        if (!section.group || !name || name === section.group.name) return;
        this.feedGroupService.update(section.group._id, { name }).pipe(take(1)).subscribe();
    }

    public cancelRename(): void {
        this.renamingId.set(null);
    }

    /** Iniciais do nome para o botão do trilho ("Clientes VIP" → "CV"). */
    public initials(name: string): string {
        return (name ?? '')
            .trim()
            .split(/\s+/)
            .slice(0, 2)
            .map(word => word[0] ?? '')
            .join('')
            .toUpperCase();
    }

    public toggleSection(section: FeedSection): void {
        this.feedGroupService.toggleCollapsed(section.id);
    }

    public onPressStart(item: FeedItemEntity, event: PointerEvent): void {
        if (event.button !== 0 || (event.target as HTMLElement).closest('.feed-row__menu')) return;
        this.pressingId.set(item._id);
    }

    public onPressEnd(): void {
        this.pressingId.set(null);
    }

    public onDragStart(event: DragEvent, item: FeedItemEntity): void {
        this.pressingId.set(null);
        this.draggingId.set(item._id);
        this.rowMenu.set(null);
        if (!event.dataTransfer) return;

        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', item._id);
        this._setDragChip(event, item);
    }

    /**
     * Imagem do arraste: em vez da linha inteira meio transparente (padrão do
     * navegador), um chip compacto — avatar + título — inclinado e com sombra,
     * como um card levantado da lista. O chip vive escondido no template e é
     * preenchido aqui, porque `setDragImage` precisa de um elemento já renderizado.
     */
    private _setDragChip(event: DragEvent, item: FeedItemEntity): void {
        const chip = this._host.nativeElement.querySelector<HTMLElement>('.feed-drag-chip');
        const row = event.currentTarget as HTMLElement | null;
        if (!chip || !row) return;

        const avatar = row.querySelector('.feed-row__avatar');
        const avatarSlot = chip.querySelector<HTMLElement>('.feed-drag-chip__avatar');
        const title = chip.querySelector<HTMLElement>('.feed-drag-chip__title');
        const subtitle = chip.querySelector<HTMLElement>('.feed-drag-chip__subtitle');
        if (avatarSlot) avatarSlot.replaceChildren(avatar ? avatar.cloneNode(true) : '');
        if (title) title.textContent = this.itemTitle(item);
        if (subtitle) {
            subtitle.textContent =
                item.type === FeedItemTypeEnum.GROUP
                    ? this._t('order.feed.documentsCount', { count: item.documentsCount })
                    : (row.querySelector('.feed-row__size')?.textContent?.trim() ?? '');
        }

        // Cursor no canto superior esquerdo: o chip fica abaixo/à direita e não
        // cobre o alvo (botão do trilho ou cabeçalho) que está sob o ponteiro.
        event.dataTransfer!.setDragImage(chip, 10, 10);
    }

    public onDragEnd(): void {
        this.draggingId.set(null);
        this.pressingId.set(null);
        this.dropTarget.set(null);
    }

    /** Alvo (seção ou botão do trilho) aceita o arraste e se realça. */
    public onDragOver(event: DragEvent, targetId: string): void {
        if (!this.draggingId()) return;
        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
        if (this.dropTarget() !== targetId) this.dropTarget.set(targetId);
    }

    public onDragLeave(targetId: string): void {
        if (this.dropTarget() === targetId) this.dropTarget.set(null);
    }

    public onDrop(event: DragEvent, targetId: string): void {
        event.preventDefault();
        const orderId = this.draggingId() ?? event.dataTransfer?.getData('text/plain');
        this.onDragEnd();
        if (!orderId) return;

        this._flyToTarget(orderId, event.currentTarget as HTMLElement | null, targetId);
        this.moveToGroup(orderId, targetId);
    }

    /**
     * O "voo": um clone da linha encolhe e voa até o alvo (botão do trilho ou
     * cabeçalho da seção), o alvo dá um pop e, em "Todos", a linha pisca na
     * seção onde caiu. Só animação — a mudança de verdade é o `moveToGroup`.
     */
    private _flyToTarget(orderId: string, dropEl: HTMLElement | null, targetId: string): void {
        if (
            typeof window === 'undefined' ||
            window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
        ) {
            this._flashLanded(orderId);

            return;
        }

        const row = this._host.nativeElement.querySelector<HTMLElement>(
            `.feed-row[data-id="${orderId}"]`
        );
        const target =
            dropEl?.closest<HTMLElement>('.feed-rail__item') ??
            dropEl?.querySelector<HTMLElement>('.feed-group__head') ??
            dropEl;
        if (!row || !target) return;

        const from = row.getBoundingClientRect();
        const to = target.getBoundingClientRect();
        const ghost = row.cloneNode(true) as HTMLElement;
        ghost.classList.add('feed-row--ghost');
        ghost.style.cssText = `position:fixed;left:${from.left}px;top:${from.top}px;width:${from.width}px;height:${from.height}px;margin:0;z-index:1000;pointer-events:none;`;
        document.body.appendChild(ghost);

        const dx = to.left + to.width / 2 - (from.left + from.width / 2);
        const dy = to.top + to.height / 2 - (from.top + from.height / 2);
        const flight = ghost.animate(
            [
                { transform: 'translate(0, 0) scale(1)', opacity: 1, offset: 0 },
                {
                    transform: `translate(${dx * 0.35}px, ${dy * 0.35}px) scale(0.7)`,
                    opacity: 0.9,
                    offset: 0.4,
                },
                { transform: `translate(${dx}px, ${dy}px) scale(0.12)`, opacity: 0, offset: 1 },
            ],
            { duration: 420, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)', fill: 'forwards' }
        );

        flight.onfinish = () => {
            ghost.remove();
            this.poppedTargetId.set(targetId);
            setTimeout(() => this.poppedTargetId.set(null), 450);
            this._flashLanded(orderId);
        };
    }

    private _flashLanded(orderId: string): void {
        this.landedId.set(orderId);
        setTimeout(() => this.landedId.set(null), 1200);
    }

    /**
     * Move o item para um grupo (`FEED_GROUP_NONE` = tira do grupo). Em "Todos" a
     * seção muda sozinha (o mapa order → grupo é reativo); num grupo filtrado o
     * item sai da lista na hora, porque deixou de pertencer ao filtro.
     */
    public moveToGroup(orderId: string, targetId: string): void {
        const request = this.feedGroupService.moveItem(orderId, targetId);
        if (!request) return;

        request.pipe(take(1)).subscribe(() => {
            const selected = this.feedMainService.groupId();
            if (selected && selected !== targetId) {
                this.feedMainService.items.update(items => items.filter(i => i._id !== orderId));
            }
        });
    }

    // ── Menu "···" da linha ────────────────────────────────────────────

    public toggleRowMenu(item: FeedItemEntity): void {
        this.rowMenu.update(menu =>
            menu?.itemId === item._id ? null : { itemId: item._id, level: 'root' }
        );
    }

    public closeRowMenu(item: FeedItemEntity): void {
        if (this.rowMenu()?.itemId === item._id) this.rowMenu.set(null);
    }

    /**
     * Itens do menu da linha. Raiz: mover para grupo, mover para ordem de serviço
     * (só avulso), arquivar e excluir. Nível "grupos": a lista de grupos + sem
     * grupo + novo grupo + voltar.
     */
    public rowMenuItems(item: FeedItemEntity): RaMenuItem[] {
        if (this.rowMenu()?.level === 'groups') return this._groupMenuItems(item);

        const items: RaMenuItem[] = [
            {
                value: 'move-group',
                label: this._t('order.feed.rowMenu.moveToGroup'),
                icon: 'folder',
            },
        ];
        if (item.type === FeedItemTypeEnum.DOCUMENT) {
            items.push({
                value: 'move-order',
                label: this._t('order.feed.rowMenu.moveToOrder'),
                icon: 'folder-open',
            });
        }
        items.push({
            value: 'archive',
            label: this._t('order.feed.rowMenu.archive'),
            icon: 'archive',
            dividerBefore: true,
        });
        items.push({
            value: 'delete',
            label: this._t('order.feed.rowMenu.delete'),
            icon: 'trash',
            variant: 'danger',
        });

        return items;
    }

    private _groupMenuItems(item: FeedItemEntity): RaMenuItem[] {
        const current = this.feedGroupService.groupByOrder().get(item._id) ?? FEED_GROUP_NONE;
        const items: RaMenuItem[] = this.feedGroupService.groups().map(group => ({
            value: `group:${group._id}`,
            label: group.name,
            icon: 'folder',
            disabled: group._id === current,
        }));

        items.push({
            value: `group:${FEED_GROUP_NONE}`,
            label: this._t('order.feed.groups.none'),
            icon: 'folder-open',
            disabled: current === FEED_GROUP_NONE,
            dividerBefore: items.length > 0,
        });
        items.push({
            value: 'new-group',
            label: this._t('order.feed.groups.newEllipsis'),
            icon: 'add',
            dividerBefore: true,
        });
        items.push({
            value: 'back',
            label: this._t('order.common.back'),
            icon: 'arrow-left',
            dividerBefore: true,
        });

        return items;
    }

    public onRowMenuSelect(item: FeedItemEntity, value: string): void {
        if (value === 'move-group') {
            this.rowMenu.set({ itemId: item._id, level: 'groups' });

            return;
        }
        if (value === 'back') {
            this.rowMenu.set({ itemId: item._id, level: 'root' });

            return;
        }

        this.rowMenu.set(null);

        if (value === 'new-group') {
            this._pendingMoveOrderId = item._id;
            this.openGroupModal();
        } else if (value.startsWith('group:')) {
            const targetId = value.slice('group:'.length);
            const target = this._host.nativeElement.querySelector<HTMLElement>(
                `.feed-rail__item[data-group="${targetId}"]`
            );
            this._flyToTarget(item._id, target, targetId);
            this.moveToGroup(item._id, targetId);
        } else if (value === 'move-order') {
            this.documentToMove.set(item);
        } else if (value === 'archive') {
            this.archiveStandalone(item);
        } else if (value === 'delete') {
            this.removeItem(item);
        }
    }

    /** Exclui (vai para a lixeira): o documento do avulso ou a ordem de serviço. */
    public removeItem(item: FeedItemEntity): void {
        const request =
            item.type === FeedItemTypeEnum.DOCUMENT
                ? item.document?._id
                    ? this._documentController.remove(item.document._id)
                    : null
                : this._orderController.remove(item._id);
        if (!request) return;

        request.pipe(take(1)).subscribe({
            next: () => {
                this.feedMainService.items.update(items => items.filter(i => i._id !== item._id));
                if (this.orderMainService.currentOrder()?._id === item._id) {
                    this.orderMainService.setOrder(null);
                }
            },
        });
    }

    /** Fecha o seletor de ordem de serviço e move o avulso para a escolhida. */
    public moveDocumentToOrder(order: OrderEntity): void {
        const item = this.documentToMove();
        this.documentToMove.set(null);
        const documentId = item?.document?._id;
        if (!documentId || !order?._id) return;

        this._documentController
            .move(documentId, order._id)
            .pipe(take(1))
            .subscribe(() => {
                // Deixou de ser avulso: some da lista; o serviço de destino muda de contagem
                this.feedMainService.items.update(items => items.filter(i => i._id !== item._id));
                this.feedMainService.refreshSilent();
            });
    }

    // ── Grupos: criar / editar / desfazer ──────────────────────────────

    public openGroupModal(group?: FeedGroupEntity): void {
        this.groupNameControl.setValue(group?.name ?? '');
        this.groupColor.set(
            group
                ? (group.color ?? null)
                : this.groupColors[this.feedGroupService.groups().length % this.groupColors.length]
        );
        this.groupModal.set({ group });
    }

    public closeGroupModal(): void {
        this.groupModal.set(null);
        this._pendingMoveOrderId = null;
    }

    public saveGroup(): void {
        const modal = this.groupModal();
        if (!modal || this.savingGroup()) return;

        this.groupNameControl.markAsTouched();
        if (this.groupNameControl.invalid) return;

        const name = this.groupNameControl.value!.trim();
        const color = this.groupColor() ?? undefined;
        const pendingMove = this._pendingMoveOrderId;
        this.savingGroup.set(true);

        const request = modal.group
            ? this.feedGroupService.update(modal.group._id, { name, color: color ?? null })
            : this.feedGroupService.create(name, color);

        request.pipe(take(1)).subscribe({
            next: group => {
                this.savingGroup.set(false);
                this.closeGroupModal();
                if (pendingMove && group?._id) this.moveToGroup(pendingMove, group._id);
            },
            error: () => this.savingGroup.set(false),
        });
    }

    /** Desfaz o grupo (com confirmação): os itens continuam, só ficam sem grupo. */
    public removeGroup(group: FeedGroupEntity): void {
        this._dialogConfirmation
            .confirm({
                title: this._t('order.feed.groups.removeTitle'),
                message: this._t('order.feed.groups.removeMessage', { name: group.name }),
                confirmText: this._t('order.feed.groups.remove'),
                cancelText: this._t('order.common.cancel'),
                type: 'danger',
            })
            .pipe(
                switchMap((control: DialogConfirmationControl) => {
                    control.setLoading(true);

                    return this.feedGroupService.remove(group._id).pipe(
                        take(1),
                        switchMap(() => {
                            control.close();
                            this.closeGroupModal();
                            // Estava olhando o grupo desfeito: volta para Todos
                            if (this.feedMainService.groupId() === group._id)
                                this.selectGroup(null);

                            return EMPTY;
                        })
                    );
                })
            )
            .subscribe({ error: () => void 0 });
    }

    /**
     * Abre um item do feed: grupo carrega a order completa e abre o detalhe;
     * avulso navega direto para o editor do documento.
     */
    public openItem(item: FeedItemEntity): void {
        if (item.type === FeedItemTypeEnum.DOCUMENT) {
            // Documento ainda em geração: o editor abriria um placeholder vazio —
            // bloqueia e avisa o motivo.
            if (this.isGenerating(item)) {
                this._toastService.warning({
                    message: this._translate.instant('order.feed.generatingWait'),
                });

                return;
            }

            if (!item.document?._id) return;
            this._documentController.navigateToDocument(item.document as DocumentEntity);

            return;
        }

        // Grupo: o detalhe (order-details) precisa da order completa — o item do
        // feed é uma projeção enxuta. Busca antes de setar como atual.
        this.openingItemId.set(item._id);
        this._orderController
            .getById(item._id)
            .pipe(take(1))
            .subscribe({
                next: order => {
                    this.openingItemId.set(null);
                    this.orderMainService.setOrder(order);
                },
                error: () => this.openingItemId.set(null),
            });
    }

    // ── Menu "···" (utilidades) ────────────────────────────────────────

    /** Despacha a opção escolhida na modal de utilidades. */
    public onMoreOptionSelect(value: string): void {
        if (value === 'archived') this.openArchived();
        else if (value === 'trash') this.openTrash();
        else if (value === 'new-group') {
            this.isMoreMenuVisible.set(false);
            this.openGroupModal();
        }
    }

    public openArchived(): void {
        this.isMoreMenuVisible.set(false);
        this.isArchivedPanelVisible.set(true);
    }

    public openTrash(): void {
        this.isMoreMenuVisible.set(false);
        this.isTrashPanelVisible.set(true);
    }

    /** Nova ordem de serviço → abre o modal de criação. */
    public newService(): void {
        this.isModalCreateOrderVisible.set(true);
    }

    /** "Importar arquivos" → abre o modal de import em modo AVULSO (orderId null). */
    public newDocumentByImport(): void {
        this.isImportDocumentsVisible.set(true);
    }

    /**
     * O import concluiu: recarrega o feed para os documentos em conversão (PENDING)
     * aparecerem. O polling de itens em geração cuida de atualizar até ficarem prontos.
     */
    public onImportCreated(): void {
        this.getFeed();
    }

    /**
     * Evento de criação de serviço realizado com sucesso
     * @param order Serviço criado
     */
    public orderCreated(order: OrderEntity): void {
        this.isModalCreateOrderVisible.set(false);
        this.orderMainService.setOrder(order);

        // Recarrega o feed para o novo serviço aparecer como item
        this.getFeed();
    }

    /**
     * Responsáveis do documento avulso no shape que o `ra-avatars-list`
     * consome (só nome + avatar importam para a bolinha).
     */
    public responsibleUsers(item: FeedItemEntity): OnlineUserPartial[] {
        return (item.document?.responsibles ?? []).map(r => ({
            _id: r.userId,
            name: r.name ?? '',
            avatar: r.avatar,
        }));
    }

    /** Título da linha: grupo = cliente (fallback descrição); avulso = título do doc. */
    public itemTitle(item: FeedItemEntity): string {
        if (item.type === FeedItemTypeEnum.GROUP) {
            return item.client?.name || item.description?.trim() || '-';
        }

        return item.document?.title || '-';
    }
}
